import { AuditLogTable } from 'src/components/molecules';
import { AuditEntryType, useGetTagsAuditEntriesQuery } from 'src/graphql';
import { formatDateTime } from 'src/utils/commonFunctions';

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const TagsAuditLogTable = ({ isOpen, setIsOpen }: Props) => {
  const { data: tagsAuditEntries } = useGetTagsAuditEntriesQuery();

  const columns = [
    { label: 'User' },
    { label: 'Tag type' },
    { label: 'Tag' },
    { label: 'Event' },
    { label: 'Timestamp' },
  ];

  const getTagName = (
    tagTypeEvent: boolean,
    action?: string,
    entry?: AuditEntryType,
  ) => {
    const tagTypeReordered =
      tagTypeEvent && action?.includes('REORDER_TAG_TYPES');
    const allTagsDeleted = entry?.data?.tagNames !== undefined;

    if (tagTypeReordered || allTagsDeleted) {
      return 'All';
    }

    return (
      entry?.data?.newName || entry?.data?.name || entry?.data?.tagName || '-'
    );
  };

  const getEventDescription = (
    action: string,
    isTagTypeEvent: boolean,
    isMultipleUpdates: boolean,
  ) => {
    const eventMapping: { [key: string]: string } = {
      ENABLE_TAG: 'enabled',
      PAUSE_TAG: 'paused',
      ACTIVATE_TAG_TYPE: 'activated',
      PAUSE_TAG_TYPE: 'paused',
      CREATE_TAG: 'created',
      UPDATE_TAG: 'updated',
      REORDER_TAGS: 'reordered',
      REORDER_TAG_TYPES: 'reordered',
      DELETE_TAG: 'deleted',
      DELETE_ALL_TAGS: 'deleted',
    };

    const baseEvent =
      eventMapping[action as keyof typeof eventMapping] ||
      action
        .replace('_TAG_TYPE', '')
        .replace('_TAGS', '')
        .replace('_TAG', '')
        .toLowerCase();
    const pluralSuffix = isMultipleUpdates ? 's' : '';
    const eventPrefix = isTagTypeEvent
      ? `Tag type${pluralSuffix}`
      : `Tag${pluralSuffix}`;

    return `${eventPrefix} ${baseEvent}`;
  };

  const tableDataCallback = (entry: AuditEntryType) => {
    const action = entry?.action;
    const tagTypeEvent = action?.includes('TAG_TYPE') ?? false;
    const multipleUpdates =
      action?.includes('TAGS') || action?.includes('TYPES');

    const event = getEventDescription(
      action || '',
      tagTypeEvent,
      multipleUpdates ?? false,
    );

    const tagName = getTagName(tagTypeEvent, action ?? undefined, entry);

    return {
      cellData: [
        entry.User?.name || entry.User?.displayName || 'System',
        entry.data?.tagTypeName || 'Untitled',
        tagName || 'Untitled',
        event,
        formatDateTime(new Date(entry.createdAt)),
      ],
      row: `${entry.id}`,
    };
  };

  return (
    <AuditLogTable
      auditLogEntries={
        (tagsAuditEntries?.tagsAuditEntries as AuditEntryType[]) ?? []
      }
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      columns={columns}
      tableDataCallback={tableDataCallback}
      emptyText="No tag audit log entries."
    />
  );
};
